import matchSorter from "match-sorter";
import type { FilterValue, IdType, Row } from "react-table";

// From match-sorter typings, not exported
type KeyOptions<T> = string | ((item: T) => string | string[]);

type ExtendedKeyOptions<T> = { key: KeyOptions<T> } & (
    | { minRanking: number }
    | { maxRanking: number }
    | { threshold: number }
);

const getThreshold = (id: IdType<any>) =>
    // Tresholds: https://github.com/kentcdodds/match-sorter#threshold-number
    id.endsWith("Id") || id === "id"
        ? matchSorter.rankings.EQUAL
        : // ACRONYM is slightly more constrained (right letters in right order is not a match anymore)
          matchSorter.rankings.MATCHES;

export function fuzzyTextFilter<T extends object>(rows: Array<Row<T>>, id: IdType<T>, filterValue: FilterValue) {
    return matchSorter(rows, filterValue, {
        keys: [
            {
                key: (row: Row<T>) => row.values[id],
                threshold: getThreshold(id),
            },
        ],
    });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilter.autoRemove = (val: any) => !val;

export function fuzzyGlobalTextFilter<T extends object>(
    rows: Array<Row<T>>,
    ids: Array<IdType<T>>,
    filterValue: FilterValue,
) {
    const terms = (filterValue as string)?.split(" ");
    if (!terms || !(typeof terms === "object")) {
        return rows;
    }

    const keys: Array<ExtendedKeyOptions<Row<T>>> = ids.map((id) => ({
        key: (row: Row<T>) => row.values[id],
        threshold: getThreshold(id),
    }));

    const filterFunction = (data: Array<Row<T>>, filterVal: string) =>
        matchSorter(data, filterVal, {
            keys,
        });

    return terms.reduceRight((results, term) => filterFunction(results, term), rows);
}

// Let the table remove the filter if the array is empty
fuzzyGlobalTextFilter.autoRemove = (val: any) => !val || val.length === 0;
